// NOT3D — page controller.
// Hero (§01/02) is a real <img> stack (120 frames of the KIT_TO_PRODUCT clip),
// exactly one un-hidden per scroll position, forward and backward — the same
// technique Apple's own product pages use for scroll-scrubbed sequences,
// chosen after <video> and <canvas> both proved unreliable for this. Pinned
// via GSAP ScrollTrigger's own `pin:true` rather than CSS position:sticky
// (some current Safari builds freeze a sticky element's painted layer and
// stop repainting it once its content starts changing). The GLB stays live
// via the reusable Not3DViewer (assets/not3d-viewer-v2.js) for the exploded
// view (§04) and the final static hero (§14).
import { Not3DViewer, isWebGLAvailable } from './not3d-viewer-v2.js';

const root = document.querySelector('.n3');
if (root) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hasPointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const webgl = isWebGLAvailable();

  const stageEl = document.getElementById('n3Stage');
  const explodedEl = document.getElementById('n3Exploded');
  const finalEl = document.getElementById('n3Final');
  const heroCopy = document.getElementById('n3HeroCopy');
  const phaseWords = Array.from(document.querySelectorAll('.n3-phase-word'));

  // Real GLB timeline checkpoints (0–10s) expressed as scroll progress:
  // hero settle → FLAT (tabs/slats begin ~t=1.5–4s) → BUILD (frame/rings,
  // ~t=4–8.5s) → OBJECT (rings lock, ~t=8.5–10s).
  const PHASES = [
    { from: 0.05, to: 0.15 }, // FLAT
    { from: 0.15, to: 0.38 }, // BREAK FREE
    { from: 0.38, to: 0.85 }, // BUILD
    { from: 0.85, to: 1.00 }, // OBJECT
  ];

  setStageHeight();
  window.matchMedia('(max-width: 768px)').addEventListener('change', setStageHeight);

  initHeroFrames();

  if (!webgl) {
    showGLBFallbacks();
  } else {
    initGLBViewers();
  }

  initReveals();

  function setStageHeight() {
    // Deliberately long: a fast trackpad flick with momentum can otherwise
    // blow through the whole pinned range in under a second, and the
    // transformation — though playing correctly frame-by-frame — reads as
    // an instant jump instead of something scroll is "doing".
    const short = window.matchMedia('(max-width: 768px)').matches;
    document.documentElement.style.setProperty('--n3-stage-vh', short ? '480vh' : '850vh');
  }

  function showGLBFallbacks() {
    [explodedEl, finalEl].forEach((section) => {
      if (!section) return;
      section.querySelectorAll('.n3-canvas').forEach((c) => { c.hidden = true; });
      section.querySelectorAll('.n3-canvas-fallback').forEach((img) => { img.hidden = false; });
    });
  }

  // ---------------------------------------------------------------------
  // Hero: real <img> stack, one un-hidden per scroll position
  // ---------------------------------------------------------------------
  function initHeroFrames() {
    const wrap = document.getElementById('n3HeroFrames');
    if (!wrap) return;
    const frameEls = Array.from(wrap.querySelectorAll('.n3-frame-img'));
    const frameCount = frameEls.length;
    if (!frameCount) return;

    let currentIndex = 0;
    const fail = () => {
      wrap.hidden = true;
      const fallback = document.getElementById('n3HeroFallback');
      if (fallback) fallback.hidden = false;
    };

    function showFrame(idx) {
      const i = Math.min(frameCount - 1, Math.max(0, idx));
      if (i === currentIndex && !frameEls[i].hidden) return;
      if (frameEls[currentIndex]) frameEls[currentIndex].hidden = true;
      frameEls[i].hidden = false;
      currentIndex = i;
    }

    setTimeout(() => { if (!(frameEls[0].complete && frameEls[0].naturalWidth)) fail(); }, 8000);

    if (reducedMotion) {
      document.documentElement.style.setProperty('--n3-stage-vh', '100vh');
      heroCopy.classList.remove('n3-is-hidden');
      phaseWords[phaseWords.length - 1].classList.add('n3-is-active');
      showFrame(frameCount - 1);
      return;
    }

    wireHeroFrameScrub(frameCount, showFrame);
  }

  function wireHeroFrameScrub(frameCount, showFrame) {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    if (!gsap || !ScrollTrigger) return;
    gsap.registerPlugin(ScrollTrigger);

    // Coalesce onUpdate calls into one frame-swap per animation frame.
    let pendingProgress = null;
    let raf = null;
    function flush() {
      raf = null;
      if (pendingProgress === null) return;
      showFrame(Math.round(pendingProgress * (frameCount - 1)));
      pendingProgress = null;
    }

    const pinEl = stageEl.querySelector('.n3-stage-pin');
    ScrollTrigger.create({
      trigger: stageEl,
      start: 'top top',
      end: 'bottom bottom',
      scrub: 0.6,
      pin: pinEl,
      pinSpacing: false, // .n3-stage-wrapper is already the tall spacer
      onUpdate(self) {
        pendingProgress = self.progress;
        if (raf === null) raf = requestAnimationFrame(flush);

        heroCopy.classList.toggle('n3-is-hidden', self.progress > 0.045);
        phaseWords.forEach((word, i) => {
          const { from, to } = PHASES[i];
          const active = self.progress >= from && self.progress < (i === PHASES.length - 1 ? to + 0.001 : to);
          word.classList.toggle('n3-is-active', active);
        });
      },
    });
  }

  // ---------------------------------------------------------------------
  // Exploded view (§04) + final static hero (§14) — the real GLB
  // ---------------------------------------------------------------------
  async function initGLBViewers() {
    const glbUrl = root.dataset.glb;

    const explodedViewer = new Not3DViewer(document.getElementById('n3ExplodedCanvas'), { mode: 'explode', toneDown: true });
    const finalViewer = new Not3DViewer(document.getElementById('n3FinalCanvas'), { mode: 'static' });

    try {
      await Promise.all([explodedViewer.load(glbUrl), finalViewer.load(glbUrl)]);
    } catch (err) {
      console.warn('NOT3D: GLB failed to load, showing poster fallback.', err);
      showGLBFallbacks();
      return;
    }

    observeVisibility(explodedEl, explodedViewer);
    observeVisibility(finalEl, finalViewer);

    if (reducedMotion) {
      [explodedViewer, finalViewer].forEach((v) => v.setActive(true));
      return;
    }

    wireExplode(explodedViewer);
    if (hasPointer) wireFinalPointer(finalViewer);
    else finalViewer.setActive(true);
  }

  function observeVisibility(el, viewer) {
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => viewer.setActive(e.isIntersecting)),
      { rootMargin: '15% 0px 15% 0px' }
    );
    io.observe(el);
  }

  function wireExplode(viewer) {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    if (!gsap || !ScrollTrigger) return;
    gsap.registerPlugin(ScrollTrigger);

    ScrollTrigger.create({
      trigger: explodedEl,
      start: 'top bottom',
      end: 'bottom top',
      scrub: 0.4,
      onUpdate(self) {
        // Triangular profile: 0 at both ends of the section, 1 while centered.
        const p = self.progress;
        const factor = p < 0.5 ? p * 2 : (1 - p) * 2;
        viewer.setExplode(factor);
      },
    });
  }

  function wireFinalPointer(viewer) {
    finalEl.addEventListener('mousemove', (e) => {
      const r = finalEl.getBoundingClientRect();
      const nx = ((e.clientX - r.left) / r.width) * 2 - 1;
      const ny = ((e.clientY - r.top) / r.height) * 2 - 1;
      viewer.setPointer(nx, ny);
    });
    finalEl.addEventListener('mouseleave', () => viewer.setPointer(0, 0));
  }

  // ---------------------------------------------------------------------
  // Editorial reveals
  // ---------------------------------------------------------------------
  function initReveals() {
    if (reducedMotion) return; // CSS already shows .reveal elements at rest.
    const els = document.querySelectorAll('.reveal');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('n3-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });
    els.forEach((el) => io.observe(el));
  }
}
